import React, { useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";

import { StatePanel } from "../../components/ui";
import useBreakpoint from "../../hooks/useBreakpoint";
import { money, moneyShort } from "../../dashboard/format";
import "./earnings-page.scss";

// The Earnings tab (2026-10-28): the signed-in user's OWN commissions in the active
// store, from GET /api/earnings/commissions. Built to the approved "Earnings page
// preview" artifact. Amounts are the P&L's — paid payout, else the agreed % of the
// deal's profit, else (no commission recorded) 100% to the deal owner = "Estimated".
//
// Paid = a payout recorded on the order AND confirmed (completed, or the order's
// Commission debit matched on the bank). Everything else is "Not paid yet" — an
// entered-but-unmatched payout reads the same as no payout (Alex, 2026-10-28).

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const PREVIEW_ROWS = 8;

const STATUS = {
  paid: "Paid",
  unpaid: "Not paid yet",
  inferred: "Estimated",
};
const statusOf = (d) => (STATUS[d.status] ? d.status : "unpaid");

const thisYear = new Date().getFullYear();
const YEARS = [0, 1, 2, 3].map((i) => thisYear - i);

// A "nice" axis ceiling: 1 / 2 / 2.5 / 5 / 7.5 / 10 × 10^n — £7.5k for a £6.9k peak.
const niceMax = (v) => {
  if (!(v > 0)) return 100000;
  const p = 10 ** Math.floor(Math.log10(v));
  const n = v / p;
  return [1, 2, 2.5, 5, 7.5, 10].find((s) => n <= s) * p;
};

const watchName = (d) => [d.brand, d.model].filter(Boolean).join(" ") || "Watch";
const shareOf = (d) => (d.share_percentage === null || d.share_percentage === undefined ? "—" : `${Math.round(d.share_percentage)}%`);
const dateOf = (d, format) => (d.date ? dayjs(d.date).format(format) : "—");

const Status = ({ deal }) => {
  const s = statusOf(deal);
  return <span className={`earn-status earn-status--${s}`}>{STATUS[s]}</span>;
};

const Chart = ({ months, year }) => {
  const max = niceMax(Math.max(0, ...months.map((m) => m.earned)));
  const peak = months.reduce((best, m) => (m.earned > best.earned ? m : best), months[0]);
  const label = `Commission earned per month in ${year}, ${MONTHS[0]} to ${MONTHS[months.length - 1]}`
    + (peak?.earned > 0 ? `; ${MONTHS[months.indexOf(peak)]} is highest at ${money(peak.earned)}` : "");

  return (
    <figure className="earn-chart" role="img" aria-label={label}>
      <div className="earn-chart__plot">
        <div className="earn-chart__axis" aria-hidden="true">
          <span>{moneyShort(max)}</span>
          <span>{moneyShort(max / 2)}</span>
          <span>{moneyShort(0)}</span>
        </div>
        <div className="earn-chart__bars">
          {months.map((m, i) => {
            const current = i === months.length - 1;
            return (
              <div key={m.month} className="earn-chart__col">
                {current && <span className="earn-chart__value">{moneyShort(m.earned)}</span>}
                <span
                  className={`earn-chart__bar${current ? " is-current" : ""}`}
                  // Data-driven geometry, the one inline style on the page.
                  style={{ height: `${Math.max(0, Math.min(100, (m.earned / max) * 100))}%` }}
                  title={`${MONTHS[i]}: ${money(m.earned)}`}
                />
              </div>
            );
          })}
        </div>
      </div>
      <div className="earn-chart__labels" aria-hidden="true">
        {months.map((m, i) => <span key={m.month}>{MONTHS[i]}</span>)}
      </div>
    </figure>
  );
};

const DealsTable = ({ deals, year }) => (
  <div className="earn-table-wrap">
    <table className="earn-table">
      <caption className="earn-sr">My commissions in {year}</caption>
      <thead>
        <tr>
          <th scope="col">Date</th>
          <th scope="col">Watch</th>
          <th scope="col">Order</th>
          <th scope="col">Client</th>
          <th scope="col" className="is-end">Share</th>
          <th scope="col">Status</th>
          <th scope="col" className="is-end">Commission</th>
        </tr>
      </thead>
      <tbody>
        {deals.map((d, i) => (
          <tr key={`${d.order_item_id}-${i}`}>
            <td className="is-dim is-nowrap">{dateOf(d, "D MMM YYYY")}</td>
            <td>
              {watchName(d)}
              {d.reference ? <span className="earn-subtle"> · {d.reference}</span> : null}
            </td>
            <td className="is-dim earn-num">{d.order_reference || "—"}</td>
            <td>{d.client || "—"}</td>
            <td className="is-end earn-num">{shareOf(d)}</td>
            <td className="is-nowrap"><Status deal={d} /></td>
            <td className="is-end is-strong earn-num">{money(d.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const DealCards = ({ deals }) => (
  <ul className="earn-deal-cards">
    {deals.map((d, i) => (
      <li key={`${d.order_item_id}-${i}`} className="earn-deal">
        <div className="earn-deal__top">
          <div className="earn-deal__name">
            <span className="earn-deal__watch">{watchName(d)}</span>
            <span className="earn-deal__sub">{[d.order_reference, d.reference].filter(Boolean).join(" · ")}</span>
          </div>
          <div className="earn-deal__right">
            <span className="earn-deal__amount">{money(d.amount)}</span>
            <Status deal={d} />
          </div>
        </div>
        <div className="earn-deal__meta">
          <span><b>Date</b>{dateOf(d, "D MMM")}</span>
          <span><b>Client</b>{d.client || "—"}</span>
          <span><b>Share</b>{shareOf(d)}</span>
        </div>
      </li>
    ))}
  </ul>
);

const MyCommissions = () => {
  const { isPhone } = useBreakpoint();
  const [year, setYear] = useState(thisYear);
  const [state, setState] = useState({ status: "loading", data: null });
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let live = true;
    setState((s) => ({ ...s, status: "loading" }));
    setShowAll(false);
    axios
      .get("/api/earnings/commissions", { params: { year } })
      .then(({ data }) => live && setState({ status: "loaded", data }))
      .catch(() => live && setState({ status: "error", data: null }));
    return () => {
      live = false;
    };
  }, [year]);

  const loaded = state.status === "loaded";
  const totals = state.data?.totals;
  // The current year stops at this month: the last bar is "now", highlighted.
  const allMonths = state.data?.months || [];
  const months = year === thisYear ? allMonths.slice(0, new Date().getMonth() + 1) : allMonths;
  const deals = state.data?.deals || [];
  const estimatedCount = deals.filter((d) => d.status === "inferred").length;
  const shown = showAll ? deals : deals.slice(0, PREVIEW_ROWS);
  const range = months.length ? `${MONTHS[0]} – ${MONTHS[months.length - 1]} ${year}` : String(year);

  return (
    <>
      <section className="earn-card" aria-labelledby="earn-commissions-title">
        <div className="earn-card__head">
          <h2 id="earn-commissions-title" className="earn-card__title">My commissions</h2>
          {loaded && <span className="earn-card__meta">{totals.deals} deal{totals.deals === 1 ? "" : "s"} in {year}</span>}
          <label className="earn-year">
            Year
            <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
        </div>

        {state.status === "error" ? (
          <StatePanel state="error" title="Couldn't load your commissions" description="Please refresh the page to try again." />
        ) : (
          <>
            <div className="earn-stats">
              <div className="earn-stat">
                <span className="earn-stat__l">Earned</span>
                <span className="earn-stat__v">{loaded ? money(totals.earned) : "—"}</span>
                <span className="earn-stat__f">{range}</span>
              </div>
              <div className="earn-stat">
                <span className="earn-stat__l">Paid out</span>
                <span className="earn-stat__v">{loaded ? money(totals.paid) : "—"}</span>
                <span className="earn-stat__f">confirmed payouts</span>
              </div>
              <div className="earn-stat">
                <span className="earn-stat__l">Not paid yet</span>
                <span className="earn-stat__v">{loaded ? money(totals.unpaid) : "—"}</span>
                <span className="earn-stat__f">
                  {loaded && totals.estimated ? `incl. ${money(totals.estimated)} estimated` : "awaiting payout"}
                </span>
              </div>
            </div>

            {loaded && months.length > 0 && <Chart months={months} year={year} />}

            {estimatedCount > 0 && (
              <p className="earn-muted">
                {estimatedCount === 1 ? "1 deal is" : `${estimatedCount} deals are`} marked Estimated: no commission was
                recorded on the order, so the full profit is shown against you as the deal owner, the same way the
                P&amp;L shows it.
              </p>
            )}
          </>
        )}
      </section>

      {state.status !== "error" && (
        <section className="earn-card earn-card--deals" aria-labelledby="earn-deals-title">
          <h2 id="earn-deals-title" className="earn-card__title">Deals</h2>
          {state.status === "loading" ? (
            <StatePanel state="loading" />
          ) : deals.length === 0 ? (
            <StatePanel
              state="empty"
              icon="Receipt"
              title="No commissions yet"
              description={`Deals you're credited on in ${year} will appear here.`}
            />
          ) : (
            <>
              {isPhone ? <DealCards deals={shown} /> : <DealsTable deals={shown} year={year} />}
              {deals.length > PREVIEW_ROWS && (
                <div className="earn-more">
                  <span>Showing {shown.length} of {deals.length} deals</span>
                  <button type="button" onClick={() => setShowAll((v) => !v)}>
                    {showAll ? "Show fewer" : "Show all"}
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      )}
    </>
  );
};

export default MyCommissions;
